// src/components/SprintStatistics/TimeTracking/TimeTrackingStatistics.jsx
import React from 'react';
import { BarChart3, Clock, Users, Target, Hash, TrendingUp, Calendar, Award } from 'lucide-react';
import StatCard from '../../common/StatCard';

const TimeTrackingStatistics = ({ statistics, filteredWorkLogs }) => {
  // Calculate unique ticket statistics properly - always call useMemo
  const calculateUniqueTicketStats = React.useMemo(() => {
    // Handle empty or null data
    if (!filteredWorkLogs || filteredWorkLogs.length === 0) {
      return { 
        totalEstimated: 0, 
        uniqueIssues: 0,
        issuesWithEstimates: 0,
        issuesWithoutEstimates: 0
      };
    }

    const uniqueIssues = new Map();
    
    filteredWorkLogs.forEach(log => {
      if (!uniqueIssues.has(log.issue_key)) {
        uniqueIssues.set(log.issue_key, {
          key: log.issue_key,
          estimatedHours: log.estimatedHours,
          summary: log.issue_summary,
          status: log.issue_status,
          priority: log.issue_priority
        });
      }
    });

    const totalEstimated = Array.from(uniqueIssues.values())
      .reduce((sum, issue) => sum + issue.estimatedHours, 0);

    return {
      totalEstimated,
      uniqueIssues: uniqueIssues.size,
      issuesWithEstimates: Array.from(uniqueIssues.values()).filter(issue => issue.estimatedHours > 0).length,
      issuesWithoutEstimates: Array.from(uniqueIssues.values()).filter(issue => issue.estimatedHours === 0).length
    };
  }, [filteredWorkLogs]);

  // Early return after hooks
  if (!statistics) return null;


  const getEfficiencyBadge = (efficiency) => {
    if (efficiency > 110) return { text: 'Over Budget', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    if (efficiency > 90) return { text: 'On Track', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' };
    if (efficiency > 70) return { text: 'Under Budget', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    return { text: 'Very Efficient', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
  };

  const getVarianceBadge = (variance) => {
    if (variance > 10) return { text: 'High Over', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    if (variance > 0) return { text: 'Over Est.', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' };
    if (variance > -10) return { text: 'Under Est.', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    return { text: 'Very Under', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
  };

  // Calculate efficiency based on unique tickets
  const uniqueTicketEfficiency = calculateUniqueTicketStats.totalEstimated > 0 ? 
    ((parseFloat(statistics.totalLogged) / calculateUniqueTicketStats.totalEstimated) * 100) : 0;

  const uniqueTicketVariance = parseFloat(statistics.totalLogged) - calculateUniqueTicketStats.totalEstimated;

  return (
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-4 flex items-center">
        <BarChart3 className="w-5 h-5 mr-2" />
        Time Tracking Statistics
      </h2>
      
      {/* Main Statistics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Log Entries"
          value={statistics.totalEntries}
          subtitle="Individual worklog entries"
          icon={Hash}
          color="blue"
          tooltip="Total number of individual time log entries"
        />
        
        <StatCard
          title="Total Hours Logged"
          value={`${statistics.totalLogged}h`}
          subtitle="Across all entries"
          icon={Clock}
          color="green"
          tooltip="Sum of all hours logged across all entries"
        />
        
        <StatCard
          title="Unique Issues"
          value={calculateUniqueTicketStats.uniqueIssues}
          subtitle={`${calculateUniqueTicketStats.issuesWithEstimates} with estimates`}
          icon={Target}
          color="orange"
          tooltip={`${calculateUniqueTicketStats.issuesWithEstimates} issues have estimates, ${calculateUniqueTicketStats.issuesWithoutEstimates} without estimates`}
        />
        
        <StatCard
          title="Team Members"
          value={statistics.uniqueAuthors || 'N/A'}
          subtitle="People who logged time"
          icon={Users}
          color="purple"
          tooltip="Number of unique people who logged time"
        />
      </div>

      {/* Estimation Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Total Estimated (Unique)"
          value={`${calculateUniqueTicketStats.totalEstimated.toFixed(1)}h`}
          subtitle="From unique tickets only"
          icon={Target}
          color="green"
          tooltip="Total estimated hours calculated from unique tickets (each ticket counted once)"
        />
        
        <StatCard
          title="Efficiency (Unique Basis)"
          value={`${uniqueTicketEfficiency.toFixed(1)}%`}
          subtitle="Logged vs Unique Estimates"
          icon={TrendingUp}
          color={uniqueTicketEfficiency > 100 ? 'red' : 'green'}
          badge={getEfficiencyBadge(uniqueTicketEfficiency)}
          tooltip="Efficiency calculated using unique ticket estimates (proper calculation method)"
        />
        
        <StatCard
          title="Variance (Unique Basis)"
          value={`${uniqueTicketVariance > 0 ? '+' : ''}${uniqueTicketVariance.toFixed(1)}h`}
          subtitle="Logged - Unique Estimates"
          icon={Award}
          color={uniqueTicketVariance > 0 ? 'red' : 'green'}
          badge={getVarianceBadge(uniqueTicketVariance)}
          tooltip="Difference between total logged hours and unique ticket estimates"
        />
      </div>

      {/* Performance Breakdown */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-white/10 mb-6">
        <h3 className="text-base font-medium text-gray-900 dark:text-slate-100 mb-3 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Estimation Quality Analysis
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {calculateUniqueTicketStats.issuesWithEstimates}
            </div>
            <div className="text-xs text-gray-600 dark:text-slate-400">Issues with Estimates</div>
            <div className="text-xs text-gray-500 dark:text-slate-500">
              ({((calculateUniqueTicketStats.issuesWithEstimates / calculateUniqueTicketStats.uniqueIssues) * 100).toFixed(1)}%)
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {calculateUniqueTicketStats.issuesWithoutEstimates}
            </div>
            <div className="text-xs text-gray-600 dark:text-slate-400">Issues without Estimates</div>
            <div className="text-xs text-gray-500 dark:text-slate-500">
              ({((calculateUniqueTicketStats.issuesWithoutEstimates / calculateUniqueTicketStats.uniqueIssues) * 100).toFixed(1)}%)
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {(calculateUniqueTicketStats.totalEstimated / calculateUniqueTicketStats.issuesWithEstimates || 0).toFixed(1)}h
            </div>
            <div className="text-xs text-gray-600 dark:text-slate-400">Avg Est. per Issue</div>
            <div className="text-xs text-gray-500 dark:text-slate-500">(with estimates only)</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {(parseFloat(statistics.totalLogged) / calculateUniqueTicketStats.uniqueIssues).toFixed(1)}h
            </div>
            <div className="text-xs text-gray-600 dark:text-slate-400">Avg Logged per Issue</div>
            <div className="text-xs text-gray-500 dark:text-slate-500">(all issues)</div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Avg Hours/Entry"
          value={`${(parseFloat(statistics.totalLogged) / statistics.totalEntries).toFixed(1)}h`}
          subtitle="Average time per log"
          icon={Clock}
          color="indigo"
          tooltip="Average hours per individual log entry"
        />
        
        {statistics.workDays && (
          <StatCard
            title="Working Days"
            value={statistics.workDays}
            subtitle={`${(parseFloat(statistics.totalLogged) / statistics.workDays).toFixed(1)}h/day avg`}
            icon={Calendar}
            color="teal"
            tooltip="Number of days with logged time"
          />
        )}
        
        {statistics.projectCount && (
          <StatCard
            title="Projects"
            value={statistics.projectCount}
            subtitle="With logged time"
            icon={Target}
            color="orange"
            tooltip="Number of unique projects with time logged"
          />
        )}
        
        <StatCard
          title="Estimation Coverage"
          value={`${((calculateUniqueTicketStats.issuesWithEstimates / calculateUniqueTicketStats.uniqueIssues) * 100).toFixed(1)}%`}
          subtitle="Issues with estimates"
          icon={Award}
          color={calculateUniqueTicketStats.issuesWithEstimates / calculateUniqueTicketStats.uniqueIssues > 0.8 ? 'green' : 'red'}
          tooltip="Percentage of issues that have time estimates"
        />
      </div>

      {/* Warning for estimation issues */}
      {calculateUniqueTicketStats.issuesWithoutEstimates > 0 && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Estimation Quality Notice
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                <p>
                  {calculateUniqueTicketStats.issuesWithoutEstimates} out of {calculateUniqueTicketStats.uniqueIssues} issues 
                  ({((calculateUniqueTicketStats.issuesWithoutEstimates / calculateUniqueTicketStats.uniqueIssues) * 100).toFixed(1)}%) 
                  don't have time estimates. Consider adding estimates to improve planning accuracy.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingStatistics;